import { addMonths, differenceInCalendarMonths, differenceInDays, differenceInMonths } from "date-fns";
import {
	DiscountMeasuringPeriodOptionLabels,
	IntervalOption,
	MonthsSelectionOptionLabels,
	TimeUnitOption,
	TimeUnitPluralOption,
} from "../../../models/simulator/options";
import { sumBy } from "@lyfegen-platform/utils";
import { IDiscountPerPack, IPacksMonthlyDiscount } from "./reporting-service";
import { isEmptyObject } from "../../../utils/is-empty-object";
import { IDistributedPack } from "./treatment-service";
import { toFixedNumber } from "../details/output/functions";
import { IPatientWeightInput } from "./inputs";

export const getDurationInMonths = (duration: number, unit: TimeUnitOption, startMonth?: Date) => {
	switch (unit) {
		case TimeUnitOption.MONTH:
			return +duration;
		case TimeUnitOption.YEAR:
			return +duration * 12;
		case TimeUnitOption.WEEK:
			return calculateMonthsFromDays(+duration * 7, startMonth);
		case TimeUnitOption.DAY:
			return calculateMonthsFromDays(duration, startMonth);
		default:
			throw Error("Unsupported duration for conversion!");
	}
};

export const calculateDurationInMonths = (startDate: string | Date, endDate: string | Date) => {
	return differenceInMonths(new Date(endDate), new Date(startDate));
};

export const getEnrollmentInput = <T extends { id: string; index?: number }>(data: Array<T>, recordId?: string) => {
	const enrollment = data.find((record) => record.id === recordId);

	if (!enrollment) throw Error("Invalid enrollment");

	return Number.isInteger(enrollment.index)
		? data.filter((record) => record.id === enrollment.id || (!record.id && record.index === enrollment.index))
		: enrollment;
};

export interface ObjectIndexer<T> {
	[key: string]: T;
}

interface IAggregationObject {
	[key: string]: number | Array<any>;
}

export const aggregateObject = (result: IAggregationObject, newValue: IAggregationObject): IAggregationObject => {
	return Object.keys(result).reduce(
		(aggregatedValue, key) => {
			if (Array.isArray(aggregatedValue[key])) {
				aggregatedValue[key] = aggregateArrays(
					aggregatedValue[key] as Array<{ value: number }>,
					newValue[key] as Array<{ value: number }>
				);
			} else {
				(aggregatedValue[key] as number) += newValue[key] as number;
			}
			return aggregatedValue;
		},
		{ ...result }
	);
};

const mergeAndSumObjects = <T extends Record<any, any>>(obj1: T, obj2: T): T => {
	const result: Partial<T> = { ...obj1 };

	for (const key in obj2) {
		if (Object.prototype.hasOwnProperty.call(obj2, key)) {
			if (key === "date") {
				result[key] = obj2[key];
			} else {
				(result as any)[key] = (result[key] || 0) + obj2[key];
			}
		}
	}

	return result as T;
};

const aggregatePackDiscountObjects = (obj1: IPacksMonthlyDiscount, obj2: IPacksMonthlyDiscount): IPacksMonthlyDiscount => {
	const result: IPacksMonthlyDiscount = { ...obj1 };
	const map: Record<string, IDiscountPerPack> = {};

	for (const pack of obj1.discountByPack) {
		map[pack.pack] = { ...pack };
	}

	for (const pack of obj2.discountByPack) {
		if (map.hasOwnProperty(pack.pack)) {
			map[pack.pack].quantity += pack.quantity;
			map[pack.pack].discount += pack.discount || 0;
			map[pack.pack].waste += pack.waste || 0;
		} else {
			if (!isEmptyObject(pack)) {
				map[pack.pack] = { ...pack };
			}
		}
	}

	result.discountByPack = Object.values(map);

	return result;
};

export const aggregateArrays = (result: Array<{ value: number }>, newValue: Array<{ value: number }>) => {
	if (!result.length) return newValue;
	return result.map((entry, index) => {
		if (entry.hasOwnProperty("discountByPack")) {
			return aggregatePackDiscountObjects(
				entry as unknown as IPacksMonthlyDiscount,
				newValue[index] as unknown as IPacksMonthlyDiscount
			);
		}
		return mergeAndSumObjects(entry, newValue[index]);
	});
};

export const getMonthNumber = (currentMonth: Date, startDate: Date) => {
	const currentMonthDate = new Date(currentMonth);
	const startDateDate = new Date(startDate);
	return currentMonthDate.getMonth() - startDateDate.getMonth() + 1 + 12 * (currentMonthDate.getFullYear() - startDateDate.getFullYear());
};

export const roundToTwo = (value: number) => Math.round(value * 1e2) / 1e2;

export function rangesOverlap(start1: number, end1: number, start2: number, end2: number): boolean {
	return Math.max(start1, start2) <= Math.min(end1, end2);
}

export const getTotalDiscountForPack = (distributedPack: IDistributedPack, inputStraightDiscount: number): number => {
	const packsDiscounts = distributedPack.discounts.map((discount) => {
		const finalDiscount = discount.scenariosDiscount.map((scenario) => {
			const adjustedDiscount = discount.isFilteredOut ? 0 : Math.min(toFixedNumber(100) - inputStraightDiscount, scenario.discount);

			return {
				...scenario,
				discount: adjustedDiscount,
			};
		});

		return { ...discount, scenariosDiscount: finalDiscount };
	});

	return Math.min(
		inputStraightDiscount +
			sumBy(
				packsDiscounts.flatMap((discount) => discount.scenariosDiscount),
				(scenario) => scenario.discount
			),
		100
	);
};

export const getMonthToReset = (period: DiscountMeasuringPeriodOptionLabels, month: MonthsSelectionOptionLabels) => {
	if (period === DiscountMeasuringPeriodOptionLabels.CALENDAR_YEAR) {
		return 11;
	}
	const monthIndex = Object.values(MonthsSelectionOptionLabels).indexOf(month);
	if (monthIndex === 0) {
		return 11;
	}
	return monthIndex - 1;
};

export const calculateMonthsFromDays = (duration: number, startMonth?: Date) => {
	if (duration <= 0 || !startMonth) {
		return 0;
	}

	let currentDate = startMonth;
	let months = 0;

	while (duration > 0) {
		const daysInMonth = differenceInDays(addMonths(currentDate, 1), currentDate);
		months++;
		duration -= daysInMonth;
		currentDate = addMonths(currentDate, 1);
	}

	return months;
};

const pluralizedUnits = {
	[TimeUnitOption.DAY]: TimeUnitPluralOption.DAYS,
	[TimeUnitOption.WEEK]: TimeUnitPluralOption.WEEKS,
	[TimeUnitOption.MONTH]: TimeUnitPluralOption.MONTHS,
	[TimeUnitOption.YEAR]: TimeUnitPluralOption.YEARS,
};

export const singleOrPluralUnit = (value?: number, unit?: TimeUnitOption) => {
	if (!value || !unit) {
		return "";
	}
	if (value > 1) {
		return pluralizedUnits[unit];
	}

	return unit;
};

export const getIndexing = (n: number): string => {
	switch (n) {
		case 1:
			return n + "st";
		case 2:
			return n + "nd";
		case 3:
			return n + "rd";
		default:
			return n + "th";
	}
};

export const getMonthsUntilReset = (
	dateString: string,
	period: DiscountMeasuringPeriodOptionLabels,
	month: MonthsSelectionOptionLabels
): number => {
	const currentDate = new Date(dateString);

	const resetMonth = getMonthToReset(period, month);
	const adjustedResetMonth = resetMonth < 11 ? resetMonth + 1 : 0;
	const currentMonth = currentDate.getMonth();
	const currentYear = currentDate.getFullYear();

	if (currentMonth > adjustedResetMonth) {
		// difference versus next year reset month
		const nextReset = new Date(currentYear + 1, adjustedResetMonth, 1);
		return differenceInCalendarMonths(nextReset, currentDate);
	} else {
		return differenceInCalendarMonths(new Date(currentYear, adjustedResetMonth, 1), currentDate);
	}
};

export const ensureDate = (input: string | number | Date) => (input instanceof Date ? input : new Date(input));

export const constructNameForWeightGroup = (group: IPatientWeightInput) => {
	if (group.startInterval === IntervalOption.FROM) {
		return `${group.startValue}-${group.endValue}kg`;
	}
	if (group.startInterval === IntervalOption.GREATER_OR_EQUAL) {
		return `>=${group.startValue}kg`;
	}
	if (group.startInterval === IntervalOption.LESS_OR_EQUAL) {
		return `<=${group.startValue}kg`;
	}

	return "";
};
